// Eklenti (filo) sayfası — tasarım C. Yalnız admin (NAV adminOnly). Tüm veri /api/live'dan:
// sürüm (agent_build_seen), heartbeat/karanlık panel, bugünkü telemetri. Yeni sunucu ucu YOK.
//
// Faz 2 (2.3.0+, migration 030; GET /api/ext-fleet): "Güncelle" / "Tümünü güncelle" (update_extension — yalnız 2.3.0+
// panel çalıştırabilir, eskiler Chrome yeniden başlatılınca güncellenir), filo "En çok görülen hatalar", rapor kutusu, ortam.
// Referans sürüm = görünen temsilcilerin en yenisi (Canlı Durum çipiyle aynı kural).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAgentCommands, getExtFleet, getLiveAgents, issueAgentCommand, setExtReportStatus } from '../api';
import { brandLabel, tzOfBrands } from '../constants';
import { AUTH_LANDING_WARN, chipHealth, ERRORS_1H_BAD, fmtDur, isLiveStatus, L, supportsKind, updateView, type UpdateTrack } from '../ext-health';
import { cmpVersion, isOutdated, newestVersion } from '../ext-version';
import { fmtClock } from '../format';
import { useNow } from '../hooks';
import type { AgentCommand, BrandId, ExtFleet as ExtFleetData, ExtReportStatus, Lang, LiveAgent } from '../types';
import { EmptyState, Icon, Skeleton } from '../ui';
import AgentCommandModal from './AgentCommandModal';
import ExtHealthDrawer from './ExtHealthDrawer';
import ExtReportModal, { installLabel, REPORT_STATUS_META } from './ExtReportModal';

const BRAND_COLOR: Record<string, string> = { vegas: '#3d8bff', prime: '#7fb0ff', primevip: '#c9a227', hera: '#5b8fd6', betbooker: '#9aa4b2' };
const C = { ok: '#37c46b', warn: '#e0a23c', bad: '#ef5d5d' };
const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px' };
const chip: React.CSSProperties = {
  fontSize: '9.5px', fontWeight: 700, color: 'var(--dim)', background: 'var(--surface2)',
  border: '1px solid var(--border)', borderRadius: '5px', padding: '2px 6px', whiteSpace: 'nowrap',
};
// One row's update_extension: `cmdId` is the server id once issued ('send-…' while the POST is out); `fromVersion` =
// the build it was sent to (a row whose version moved on is no longer this command's story); `doneAt` = local clock.
type UpdRow = UpdateTrack & { cmdId: string; fromVersion: string; doneAt?: number };
const CMD_POLL_MS = 5_000;
const CMD_TRACK_MS = 16 * 60_000;      // > the server's 15-min command TTL: an undelivered one ends 'expired'
const APPLY_WATCH_MS = 5 * 60_000;     // the panel's 4-min idle wait + the first beat on the new build
const TONE = { info: 'var(--dim)', warn: C.warn, bad: C.bad } as const;
const openLink: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11.5px', color: 'var(--accent)', cursor: 'pointer',
  background: 'transparent', border: 'none', padding: 0, flex: 'none',
};

export default function ExtFleet({ brands, lang }: { brands: BrandId[]; lang: Lang }) {
  const [agents, setAgents] = useState<LiveAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [verFilter, setVerFilter] = useState<string | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [cmdTarget, setCmdTarget] = useState<LiveAgent | null>(null);
  const [fleet, setFleet] = useState<ExtFleetData | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  // update_extension izi (temsilci → komut durumu/sonucu; metin updateView'da) ve toplu güncelleme onayı.
  const [upd, setUpd] = useState<Record<string, UpdRow>>({});
  const [confirmAll, setConfirmAll] = useState(false);
  // Per-row Güncelle is tier 2 (it reloads the agent's panel): a first click arms it, a second sends.
  const [armedId, setArmedId] = useState<string | null>(null);
  const alive = useRef(true);
  const now = useNow(15000);

  // A string key: the brands prop may be a fresh array on every parent render.
  const brandKey = brands.join(',');
  const load = useCallback(async () => {
    try {
      const [rows, f] = await Promise.all([getLiveAgents(), getExtFleet(brandKey ? brandKey.split(',') : []).catch(() => null)]);
      if (alive.current) { setAgents(rows); setFleet(f); setErr(''); }
    } catch (e) {
      if (alive.current) setErr(e instanceof Error ? e.message : 'load_failed');
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [brandKey]);
  useEffect(() => {
    alive.current = true;
    void load();
    const t = window.setInterval(() => { if (!document.hidden) void load(); }, 30_000);
    return () => { alive.current = false; window.clearInterval(t); };
  }, [load]);

  const scoped = useMemo(() => agents.filter((a) => a.brands.some((b) => brands.includes(b))), [agents, brands]);
  // Same rule as Canlı Durum: the newest build across EVERY agent, never the brand filter — one extension serves
  // every brand, and filtered to BetBooker (stuck on 1.0.x) the page called 1.0.20 "en yeni".
  const newest = newestVersion(agents.map((a) => a.extVersion || ''));
  const withVer = scoped.filter((a) => a.extVersion);
  const unseen = scoped.length - withVer.length;
  const outdatedAll = withVer.filter((a) => isOutdated(a.extVersion || '', newest));
  const brandsShown = (['vegas', 'prime', 'primevip', 'hera', 'betbooker'] as const).filter((b) => scoped.some((a) => a.brands.includes(b)));

  // Sürüm dağılımı: sürüm başına, markaya bölünmüş (çok markalı temsilci ilk markasında sayılır).
  const versions = useMemo(() => {
    const m = new Map<string, Record<string, number>>();
    for (const a of withVer) {
      const v = a.extVersion as string;
      const rec = m.get(v) || {};
      const b = a.brands.find((x) => brands.includes(x)) || a.brands[0];
      rec[b] = (rec[b] || 0) + 1;
      m.set(v, rec);
    }
    return [...m.entries()].sort((x, y) => cmpVersion(y[0], x[0]));
  }, [withVer, brands]);
  const maxCount = Math.max(1, ...versions.map(([, r]) => Object.values(r).reduce((s, n) => s + n, 0)));

  // Şimdi bakılmalı: karanlık panel, kayıp sonuç, giriş döngüsü, yazma hatası, düşen çağrı olayı.
  const attention = useMemo(() => {
    const out: { a: LiveAgent; icon: string; color: string; what: string; rank: number }[] = [];
    for (const a of scoped) {
      const t = a.telemetry;
      if (a.panelDark) {
        const ms = a.lastBeatAt ? now - new Date(a.lastBeatAt).getTime() : null;
        out.push({ a, icon: 'support', color: C.warn, rank: 1, what: ms != null ? L(lang, 'Panel karanlık · ' + fmtDur(ms, lang) + ' sessiz', 'Panel dark · silent ' + fmtDur(ms, lang)) : L(lang, 'Panel karanlık', 'Panel dark') });
      }
      if (t.deadLetters > 0) out.push({ a, icon: 'error', color: C.bad, rank: 0, what: L(lang, 'Bugün ' + t.deadLetters + ' sonuç kayboldu', t.deadLetters + ' results lost today') });
      const e1h = a.extErrors1h || 0;
      if (e1h >= ERRORS_1H_BAD) out.push({ a, icon: 'bug_report', color: C.bad, rank: 0, what: L(lang, 'Son 1 saatte ' + e1h + ' eklenti hatası', e1h + ' extension errors in the last hour') });
      if ((t.authLandings || 0) >= AUTH_LANDING_WARN) out.push({ a, icon: 'login', color: C.warn, rank: 2, what: L(lang, 'Bugün ' + t.authLandings + ' kez giriş sayfasına atıldı', 'Sent to the login page ' + t.authLandings + ' times today') });
      if (t.writeErrors > 0) out.push({ a, icon: 'edit_off', color: C.warn, rank: 3, what: L(lang, 'Bugün ' + t.writeErrors + ' yazma hatası', t.writeErrors + ' write errors today') });
      if ((t.callEventDrops || 0) > 0) out.push({ a, icon: 'call_end', color: C.warn, rank: 4, what: L(lang, (t.callEventDrops || 0) + ' çağrı olayı gönderilemedi', (t.callEventDrops || 0) + ' call events dropped') });
    }
    return out.sort((x, y) => x.rank - y.rank || x.a.name.localeCompare(y.a.name, 'tr'));
  }, [scoped, now, lang]);

  const outdated = outdatedAll
    .filter((a) => !verFilter || a.extVersion === verFilter)
    .sort((x, y) => cmpVersion(x.extVersion || '', y.extVersion || '') || x.name.localeCompare(y.name, 'tr'));
  const panelLabel = (a: LiveAgent) => (a.panelDark ? L(lang, 'Karanlık', 'Dark') : a.state === 'offline' ? L(lang, 'Kapalı', 'Closed') : a.state === 'break' ? L(lang, 'Molada', 'On break') : L(lang, 'Açık', 'Open'));

  const drawerAgent = drawerId ? scoped.find((a) => a.id === drawerId) || null : null;
  // update_extension yalnız açık bir 2.3.0+ panelde çalışır (eskiler: Chrome yeniden başlatılınca).
  const canUpdate = (a: LiveAgent) => supportsKind(a.extVersion, 'update_extension') && !a.panelDark && a.state !== 'offline';
  // A row whose update is still on its way is not re-sent by "Tümünü güncelle" (the server would refuse it as
  // command_in_flight and the row would lose its live status).
  const inFlight = (a: LiveAgent) => { const x = upd[a.id]; return !!x && x.fromVersion === (a.extVersion || '') && !updateView(x, lang).retry; };
  const updatable = outdatedAll.filter((a) => canUpdate(a) && !inFlight(a));
  const patchUpd = (id: string, cmdId: string, p: Partial<UpdRow>) =>
    setUpd((u) => (u[id]?.cmdId === cmdId ? { ...u, [id]: { ...u[id], ...p } } : u));   // a newer send owns the row
  // Follows ONE command to its terminal state (delivery rides the panel's poll, ~30-60 s; the TTL is 15 min), then
  // refreshes the list. "Gönderildi" alone used to be the whole answer — a no_update / throttled / refusal never showed.
  const track = async (id: string, cmdId: string) => {
    const until = Date.now() + CMD_TRACK_MS;
    while (alive.current && Date.now() < until) {
      await new Promise((r) => window.setTimeout(r, CMD_POLL_MS));
      if (!alive.current) return;
      let cmd: AgentCommand | undefined;
      try { cmd = (await getAgentCommands(id)).commands.find((c) => c.id === cmdId); } catch { continue; }
      if (!cmd) continue;
      const live = isLiveStatus(cmd.status);
      // doneAt is THIS browser's clock (the apply window is measured against Date.now(), never the server's).
      patchUpd(id, cmdId, { status: cmd.status, result: cmd.result, ...(live ? {} : { doneAt: Date.now() }) });
      if (!live) { void load(); return; }
    }
  };
  const updateOne = async (a: LiveAgent) => {
    const tmp = 'send-' + Date.now();
    setUpd((u) => ({ ...u, [a.id]: { cmdId: tmp, status: 'sending', fromVersion: a.extVersion || '' } }));
    try {
      const r = await issueAgentCommand(a.id, 'update_extension');
      setUpd((u) => (u[a.id]?.cmdId === tmp ? { ...u, [a.id]: { ...u[a.id], cmdId: r.command.id, status: r.command.status } } : u));
      void track(a.id, r.command.id);
    } catch (e) {
      patchUpd(a.id, tmp, { status: 'send_failed', sendError: e instanceof Error ? e.message : '' });
    }
  };
  // While an update_available waits to install (≤ 4 min idle wait + the first beat on the new build), the list
  // refreshes every 10 s so the row drops as soon as the new version is seen; past the window it says so.
  const applying = Object.values(upd).some((x) => !x.applyTimedOut && updateView(x, lang).applying);
  useEffect(() => {
    if (!applying) return undefined;
    const t = window.setInterval(() => {
      if (document.hidden) return;
      void load();
      setUpd((u) => {
        let n = u;
        for (const [id, x] of Object.entries(u)) {
          if (!x.applyTimedOut && x.doneAt && Date.now() - x.doneAt > APPLY_WATCH_MS && updateView(x, lang).applying) {
            n = { ...n, [id]: { ...x, applyTimedOut: true } };
          }
        }
        return n;
      });
    }, 10_000);
    return () => window.clearInterval(t);
  }, [applying, load, lang]);
  const updateAll = async () => {
    setConfirmAll(false);
    for (const a of updatable) await updateOne(a);
  };
  const setReport = async (id: string, status: ExtReportStatus) => {
    try { await setExtReportStatus(id, status); void load(); } catch { /* bir sonraki yenilemede */ }
  };
  const tz = tzOfBrands(brands);
  const manual = (id: string) => { const t = fleet?.env[id]?.installType; return t === 'development' || t === 'sideload'; };
  const nextStatus: Record<ExtReportStatus, ExtReportStatus> = { new: 'looking', looking: 'fixed', fixed: 'new' };
  const spark = (arr: number[]) => {
    const mx = Math.max(...arr, 1);
    return arr.map((v, i) => `${(i * 84) / Math.max(arr.length - 1, 1)},${22 - (v / mx) * 20}`).join(' ');
  };

  if (loading && !agents.length) return <Skeleton heights={[60, 220, 160]} />;
  if (err && !agents.length) return <EmptyState icon="error" title={L(lang, 'Okunamadı', 'Could not load')} reason={err} />;
  if (!scoped.length) return <EmptyState icon="extension" title={L(lang, 'Temsilci yok', 'No agents')} reason={L(lang, 'Üstteki marka filtresinden en az bir marka seçin.', 'Select at least one brand in the filter above.')} />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12.5px', color: 'var(--dim)' }}>
          {newest
            ? L(lang, 'En yeni görülen sürüm ', 'Newest version seen ')
            : L(lang, 'Henüz sürüm görülmedi', 'No version seen yet')}
          {newest && <span style={{ color: 'var(--text)', fontWeight: 600 }}>{'v' + newest}</span>}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: 'var(--faint)', maxWidth: '520px', textAlign: 'right' }}>
          Chrome güncellemeyi birkaç saat içinde kendisi yapar; hemen almak için temsilci Chrome'u yeniden başlatmalı.
        </span>
        {confirmAll ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text)' }}>{L(lang, updatable.length + ' panel güncellenecek ve yeniden yüklenecek', updatable.length + ' panels will update and reload')}</span>
            <button onClick={() => void updateAll()} style={{ padding: '8px 12px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Güncelle</button>
            <button onClick={() => setConfirmAll(false)} style={{ padding: '8px 10px', background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Vazgeç</button>
          </span>
        ) : (
          <button
            onClick={() => setConfirmAll(true)}
            disabled={!updatable.length}
            title={updatable.length ? undefined : L(lang, 'Uzaktan güncelleme 2.3.0+ ve açık panel ister', 'Remote update needs 2.3.0+ and an open panel')}
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600, border: 'none', cursor: updatable.length ? 'pointer' : 'not-allowed', background: updatable.length ? 'var(--accent)' : 'var(--border)', color: updatable.length ? '#fff' : 'var(--placeholder)' }}
          >
            <Icon name="system_update_alt" size={17} />
            <span>{L(lang, 'Tümünü güncelle (' + updatable.length + ')', 'Update all outdated (' + updatable.length + ')')}</span>
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '14px' }}>
        {/* Sürüm dağılımı */}
        <div style={{ ...card, padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '14px', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '160px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Sürüm dağılımı</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>
                {L(lang, withVer.length + ' temsilci · ' + outdatedAll.length + ' geride', withVer.length + ' agents · ' + outdatedAll.length + ' behind')
                  + (unseen ? L(lang, ' · ' + unseen + ' sürümü görülmedi', ' · ' + unseen + ' never seen') : '')}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {brandsShown.map((b) => (
                <span key={b} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--dim)' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: BRAND_COLOR[b] }} />
                  {brandLabel(b)}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {versions.map(([v, rec]) => {
              const total = Object.values(rec).reduce((s, n) => s + n, 0);
              const isNewest = v === newest;
              const on = verFilter === v;
              return (
                <div
                  key={v}
                  onClick={isNewest ? undefined : () => setVerFilter(on ? null : v)}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '5px 8px', margin: '0 -8px', borderRadius: '8px', cursor: isNewest ? 'default' : 'pointer', background: on ? 'rgba(61,139,255,.1)' : 'transparent' }}
                >
                  <span style={{ width: '56px', fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{v}</span>
                  <div style={{ flex: 1, height: '18px', background: 'var(--inset)', borderRadius: '5px', overflow: 'hidden', display: 'flex' }}>
                    {brandsShown.map((b) => (rec[b] ? (
                      <div key={b} title={brandLabel(b) + ' · ' + rec[b]} style={{ width: (rec[b] / maxCount) * 100 + '%', background: BRAND_COLOR[b], borderRight: '1px solid var(--inset)' }} />
                    ) : null))}
                  </div>
                  <span style={{ width: '26px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{total}</span>
                  <span style={{ width: '70px', fontSize: '10px', fontWeight: 700, letterSpacing: '.03em', color: isNewest ? C.ok : C.warn }}>
                    {isNewest ? 'EN YENİ' : 'GERİDE'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Şimdi bakılmalı */}
        <div style={{ ...card, padding: '16px 18px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Şimdi bakılmalı</div>
          <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '10px' }}>Her heartbeat ile güncellenir · sayaçlar bugün 00:00'dan beri</div>
          {attention.length === 0 ? (
            <div style={{ padding: '14px 0', fontSize: '12px', color: C.ok, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Icon name="check_circle" size={16} />
              <span>Şu an bakılacak bir şey yok</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {attention.map((x, i) => (
                <div key={x.a.id + x.icon + i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '1px solid var(--rowline)' }}>
                  <Icon name={x.icon} size={18} color={x.color} />
                  <span style={{ width: '96px', flex: 'none', fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.a.name}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '12px', color: 'var(--dim)' }}>{x.what}</span>
                  <button onClick={() => setDrawerId(x.a.id)} style={openLink}><span>Aç</span><Icon name="arrow_forward" size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Eski sürümdekiler */}
        <div style={{ ...card, overflow: 'hidden', gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Eski sürümdeki temsilciler</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>
                {verFilter
                  ? L(lang, outdated.length + ' temsilci v' + verFilter + ' sürümünde', outdated.length + ' on v' + verFilter)
                  : L(lang, outdatedAll.length + ' temsilci v' + newest + ' sürümünün gerisinde', outdatedAll.length + ' agents behind v' + newest)}
              </div>
            </div>
            {verFilter && (
              <button onClick={() => setVerFilter(null)} style={{ padding: '6px 10px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--dim)', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer' }}>
                Tümünü göster
              </button>
            )}
          </div>
          {outdated.length === 0 ? (
            <div style={{ padding: '16px 18px', fontSize: '12px', color: C.ok, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Icon name="check_circle" size={16} />
              <span>Herkes en yeni sürümde</span>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 96px 110px 150px', gap: '10px', padding: '9px 18px', borderBottom: '1px solid var(--border)', fontSize: '10px', fontWeight: 600, letterSpacing: '.05em', color: 'var(--faint)' }}>
                <span>TEMSİLCİ</span><span>SÜRÜM</span><span>PANEL</span><span>SAĞLIK</span><span />
              </div>
              {outdated.map((a) => {
                // Sürüm dışı nedenler: eski sürüm zaten bu listenin konusu.
                const others = chipHealth(a, false, newest, lang).reasons;
                const hc = others.some((r) => r.level === 'bad') ? C.bad : others.length ? C.warn : 'var(--faint)';
                const u = upd[a.id] && upd[a.id].fromVersion === (a.extVersion || '') ? upd[a.id] : null;
                const uv = u ? updateView(u, lang) : null;
                return (
                  <div key={a.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 150px 96px 110px 150px', gap: '10px', alignItems: 'center', padding: '9px 18px', borderBottom: '1px solid var(--rowline)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                      <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                      {a.brands.map((b) => <span key={b} style={chip}>{brandLabel(b)}</span>)}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      <span style={{ fontSize: '12px', color: C.warn, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{'v' + a.extVersion}</span>
                      {manual(a.id) && <span style={{ fontSize: '10px', color: 'var(--faint)', whiteSpace: 'nowrap' }}>{installLabel(fleet?.env[a.id]?.installType, lang)}</span>}
                    </span>
                    <span style={{ fontSize: '11.5px', color: a.panelDark ? C.warn : 'var(--dim)' }}>{panelLabel(a)}</span>
                    <span title={others.map((r) => r.text).join('\n') || undefined} style={{ fontSize: '11.5px', color: hc }}>
                      {others.length ? L(lang, others.length + ' uyarı', others.length + ' warnings') : L(lang, 'Sorun yok', 'Fine')}
                    </span>
                    <span style={{ justifySelf: 'end', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {(!uv || uv.retry) && canUpdate(a) && (
                        <button
                          onClick={() => {
                            if (armedId !== a.id) { setArmedId(a.id); window.setTimeout(() => { if (alive.current) setArmedId((x) => (x === a.id ? null : x)); }, 4000); return; }
                            setArmedId(null); void updateOne(a);
                          }}
                          title={L(lang, 'Temsilcinin paneli yeniden yüklenir', "Reloads the agent's panel")}
                          style={{ padding: '5px 11px', background: armedId === a.id ? 'rgba(127,176,255,.12)' : 'transparent', border: '1px solid var(--accent-border)', borderRadius: '7px', color: 'var(--accent-text)', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer' }}>
                          {armedId === a.id ? L(lang, 'Onayla?', 'Confirm?') : L(lang, 'Güncelle', 'Update')}
                        </button>
                      )}
                      <button onClick={() => setDrawerId(a.id)} style={openLink}><span>Aç</span><Icon name="arrow_forward" size={14} /></button>
                    </span>
                    {/* The update's live status on its own full-width line: the action column stays 150 px at any width. */}
                    {uv && <span style={{ gridColumn: '1 / -1', justifySelf: 'end', marginTop: '-4px', fontSize: '11px', color: TONE[uv.tone], textAlign: 'right' }}>{uv.text}</span>}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* En çok görülen hatalar (faz 2: ext_log_lines, bugün + 7 günlük eğilim) */}
        {fleet && (
          <div style={{ ...card, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>En çok görülen hatalar</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>Bugün · 7 günlük eğilim · 2.3.0 ve sonrası gönderir</div>
            </div>
            {fleet.errors.length === 0 ? (
              <div style={{ padding: '16px 18px', fontSize: '12px', color: 'var(--faint)' }}>Bugün eklentiden hata gelmedi.</div>
            ) : fleet.errors.map((e) => {
              const c = e.level === 'error' ? C.bad : C.warn;
              return (
                <div key={e.fp} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 44px 84px', gap: '12px', alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid var(--rowline)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {(e.cat ? '[' + e.cat + '] ' : '') + e.message}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--faint)' }}>
                      {L(lang, (e.agents ?? 0) + ' temsilci', (e.agents ?? 0) + ' agents') + (e.versions.length ? ' · ' + e.versions.map((v) => 'v' + v).join(', ') : '')}
                    </div>
                  </div>
                  <span style={{ textAlign: 'right', fontSize: '14px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{e.countToday}</span>
                  <svg viewBox="0 0 84 24" style={{ width: '84px', height: '24px', display: 'block', overflow: 'visible' }} aria-hidden="true">
                    <polyline points={spark(e.daily || [])} fill="none" stroke={c} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                  </svg>
                </div>
              );
            })}
          </div>
        )}

        {/* Rapor kutusu (faz 2: "Sorun bildir" + anlık görüntüler) */}
        {fleet && (
          <div style={{ ...card, overflow: 'hidden', gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Rapor kutusu</div>
                <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>Eklentideki "Sorun bildir" ve anlık görüntüler · durum raporu otomatik eklenir</div>
              </div>
              {fleet.reports.filter((r) => r.status === 'new').length > 0 && (
                <span style={{ fontSize: '11px', fontWeight: 700, color: C.bad, background: 'rgba(239,93,93,.12)', border: '1px solid rgba(239,93,93,.3)', borderRadius: '999px', padding: '2px 9px' }}>
                  {L(lang, fleet.reports.filter((r) => r.status === 'new').length + ' yeni', fleet.reports.filter((r) => r.status === 'new').length + ' new')}
                </span>
              )}
            </div>
            {fleet.reports.length === 0 ? (
              <div style={{ padding: '16px 18px', fontSize: '12px', color: 'var(--faint)' }}>Henüz rapor yok.</div>
            ) : fleet.reports.map((r) => {
              const m = REPORT_STATUS_META[r.status];
              return (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '150px 92px minmax(0,1fr) 210px 110px 50px', gap: '14px', alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid var(--rowline)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.agentName}</span>
                    <span style={chip}>{brandLabel(r.brandKey)}</span>
                  </span>
                  <span style={{ fontSize: '11.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                    {now - new Date(r.createdAt).getTime() < 20 * 3600_000 ? fmtClock(r.createdAt, tz)
                      : new Date(r.createdAt).toLocaleDateString(lang === 'en' ? 'en-GB' : 'tr-TR', { day: '2-digit', month: '2-digit', timeZone: tz }) + ' ' + fmtClock(r.createdAt, tz)}
                  </span>
                  <span style={{ fontSize: '12.5px', color: r.note ? 'var(--text)' : 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.note || (r.source === 'admin' ? L(lang, 'Anlık görüntü (admin)', 'Snapshot (admin)') : '')}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: 'var(--dim)', minWidth: 0 }}>
                    <Icon name="attach_file" size={15} color="var(--faint)" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(r.imageCount ? L(lang, r.imageCount + ' görüntü · ', r.imageCount + (r.imageCount === 1 ? ' image · ' : ' images · ')) : '') + L(lang, r.lineCount + ' satır', r.lineCount + ' lines') + ' · ' + Math.max(1, Math.round(r.bytes / 1024)) + ' KB' + (r.version ? ' · v' + r.version : '')}
                    </span>
                  </span>
                  <button
                    onClick={() => void setReport(r.id, nextStatus[r.status])}
                    title={L(lang, 'Durumu değiştir', 'Change status')}
                    style={{ justifySelf: 'end', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '999px', color: m.color, background: m.color + '1a', border: '1px solid ' + m.color + '40', cursor: 'pointer' }}
                  >
                    {L(lang, m.tr, m.en)}
                  </button>
                  <button onClick={() => setReportId(r.id)} style={{ ...openLink, justifySelf: 'end' }}><span>Aç</span><Icon name="arrow_forward" size={14} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {reportId && <ExtReportModal id={reportId} lang={lang} onClose={() => setReportId(null)} onChanged={() => void load()} />}

      {drawerAgent && (
        <ExtHealthDrawer
          agent={drawerAgent}
          newest={newest}
          outdated={isOutdated(drawerAgent.extVersion || '', newest)}
          lang={lang}
          onClose={() => setDrawerId(null)}
          onOpenAllCommands={() => setCmdTarget(drawerAgent)}
        />
      )}
      {cmdTarget && (
        <AgentCommandModal
          agentId={cmdTarget.id}
          agentName={cmdTarget.name}
          panelDark={cmdTarget.panelDark}
          lastBeatAt={cmdTarget.lastBeatAt}
          onClose={() => setCmdTarget(null)}
        />
      )}
    </>
  );
}
